/**
 * 이상한 방 - 시드 기반 베이스 룸 생성기 (그룹 배치)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3.1절(셀 점유) · 9.1절(꾸미기가 유일한 난제)
 * 배치 술어·레시피: `furnitureGroups.ts`
 *
 * 방은 텅 빈 채로 시작하지 않는다. 그리고 스피드런·일일 챌린지 때문에
 * **같은 시드는 같은 방을 만들어야 한다.** 그래서 여기엔 `Math.random()`이 한 줄도 없다.
 *
 * ## 개별 배치에서 그룹 배치로 (이 파일의 방향 전환)
 *
 * 이전 버전은 태그와 셀 충돌만 보고 오브젝트를 개별로 흩뿌려 20m × 20m에 380여 개를 채웠다.
 * 규칙 위반은 없었는데 플레이 평가는 "중구난방이라 뭘 외워야 할지 모르겠다"였다. 두 가지가 틀렸다.
 *
 *  1. **개수**. 요소 380개에 변화 12개는 3%다. 통상적인 틀린그림찾기는 요소 30~50에 5~7개(10~20%)다.
 *     그래서 목표 개수를 **파라미터로 받는다**(레벨 1의 15개부터 레벨 10의 110개까지).
 *     실제 집은 방 하나에 눈에 보이는 물건이 20~30개다. 구획당 43개는 창고였다.
 *  2. **구조**. 사람은 개별 물체가 아니라 패턴에서 벗어난 것을 찾고, 기억은 의미 단위로 형성된다.
 *     그래서 배치 단위를 오브젝트에서 **가구 그룹**으로 올린다 - 앵커를 먼저 놓고
 *     나머지는 관계(옆·앞·위·둘레)로 유도한다. 자세한 근거는 `furnitureGroups.ts` 상단.
 *
 * ## 배치 순서
 *
 *  1. 구획별 **예산 배분** - 목표 개수를 면적과 균등분의 절반씩 섞어 나눈다
 *  2. 우선순위 낮은 그룹부터 **한 바퀴씩 돌아가며** 배치(라운드 로빈).
 *     가중 추첨이 아니라 라운드 로빈인 이유: 추첨은 한 구획에 소파 세트만 세 번 넣을 수 있고,
 *     그러면 "구획마다 성격이 다르다"가 무너진다
 *  3. 예산이 남으면 2차 순회로 채운다. **남은 예산보다 큰 그룹은 시작하지 않는다** -
 *     개수를 맞추려고 그룹을 반토막 내면 정돈이 깨지고, 그게 이 작업이 고치려는 바로 그 증상이다
 *
 * ## 소품은 바닥에 흩뿌리지 않는다
 *
 * 바닥에 널브러진 컵은 그게 정상인지 이상한지 **판단할 기준이 없다.** 그래서 `surface` 등급은
 * `onSurface`(가구 상판)와 `wallMounted`(벽) 관계로만 들어간다. 레시피 데이터가 그것을 강제하고,
 * 이 파일은 레시피가 말한 자리에만 놓는다.
 *
 * ## 다른 파일과의 계약
 *
 *  - 배치·제거는 **전부 `verbs.ts`를 경유한다.** 셀 점유 규칙(3.1절)을 우회하면 "화면엔 있는데
 *    점유 맵엔 없는 오브젝트"가 생겨 이후 판정이 전부 거짓이 된다.
 *  - `placedBy: 'system'`으로 표시하되 **변화 후보에서 제외하지 않는다** -
 *    베이스도 바꿀 수 있어야 "그건 원래 있었다니까?"가 성립한다(컨셉시트 5절).
 *
 * ## 용어
 *
 * 이 파일에서 **zone**은 `floorPlan.Room`(구획)을 가리키며 `zoneIndex`는 `plan.rooms`의 인덱스다.
 * `RoomState`·`createRoom`의 'room'(방 전체)과 뒤섞이지 않게 낱말을 갈라 뒀다.
 */

import type {
  CatalogEntry,
  Cell3,
  InstanceId,
  ObjectInstance,
  PlayerId,
  RoomConfig,
  RoomState,
  Rot,
} from './types'
import {
  doorwayCells,
  footprintKey,
  wallFootprintCells,
  type CellRect,
  type FloorPlan,
  type Room,
} from './floorPlan'
import { cellKey, occupiedCells, rotatedFootprint } from './grid'
import { compareCodePoint } from './order'
import { createRoom, type Catalog } from './room'
import { applyAdd, applyRemove, type VerbContext } from './verbs'
import { PALETTE_SIZE } from '../catalog/palette'
import { createRng, type Rng } from './rng'
import {
  HOME_RECIPES,
  recipeMinSize,
  relationAnchorRole,
  resolveRelation,
  type Candidate,
  type EntrySelector,
  type GroupAnchor,
  type GroupRecipe,
  type GroupSlot,
  type RecipeBook,
  type SizeClassName,
  type SlotRole,
  type WallSide,
} from './furnitureGroups'

/**
 * 평면(x, z) 컬럼 키. 벽·문은 바닥부터 천장까지 수직으로 이어지므로 높이를 뺀 2D로 다룬다.
 * 값은 `floorPlan.ts`의 `footprintKey`가 만들며, `grid.ts`의 3D `cellKey`와는 세그먼트 수가
 * 달라 서로 섞일 수 없다.
 */
type ColumnKey = string

/** 생성 옵션. 평면도가 2D라 높이 기준만 밖에서 받는다. */
export type GenerateOptions = Readonly<{
  /** 오브젝트가 놓이는 바닥 셀의 y. 평면도의 벽이 y=0부터 서므로 기본값도 0이다. */
  floorY?: number
  /**
   * 목표 오브젝트 수. **레벨 시스템이 30~180을 넘긴다.**
   * 상한이지 보장이 아니다 - 그룹이 들어갈 자리가 없으면 이보다 적게 나온다.
   */
  targetObjectCount?: number
  /** 구획 태그별 그룹 레시피. 다른 컨셉의 맵(사파리·우주)은 이 책만 바꿔 끼운다. */
  recipes?: RecipeBook
}>

/* --- 상수 ------------------------------------------------------------------ */

const DEFAULT_FLOOR_Y = 0
const INSTANCE_ID_PREFIX = 'gen-'
const INSTANCE_ID_DIGITS = 4
/**
 * 9.2절: 시스템 배치는 사람 배치와 같은 취급을 받되 출처만 구분된다.
 *
 * 공개하는 이유는 꾸미기(M10)다 - 사람이 놓다 만 방을 프리셋으로 채울 때 그 물건들의 주인이 이 값이어야
 * 인당 배치 한도(`core/verbs.applyAdd`)에 걸리지 않고, 리빌의 "누가 놓았나" 문장도 시스템 것을 걸러 낸다.
 * 문자열을 저쪽에 다시 적으면 그날부터 두 값이 갈릴 수 있다.
 */
export const SYSTEM_PLACER: PlayerId = 'system'

/**
 * 목표 개수 기본값. 아파트 9구획에 **구획당 20개**에 해당한다. 한때 레벨 10의 값이었지만 단계 표는
 * 튜토리얼 난이도로 더 덜어졌고(`game/levels.ts` - 10단계 110개), 이 값은 **졸업한 사람의 방**으로 남았다 -
 * 매일의 방과 레벨 시스템이 없는 호출부(테스트·개발 도구)가 쓴다.
 */
const DEFAULT_TARGET_OBJECT_COUNT = 180

/**
 * 구획 **하나**에 채울 소품 수. 평면도가 구획을 붙여 커지는 판이 목표를 여기에 곱해 잡는다
 * (여럿이 - `core/partyPlan`의 블록 하나가 한 사람 몫이고, `game/match/matchSelectors.roomOptionsFor`가 곱한다).
 *
 * 고정 목표(180)를 그런 평면도에 그대로 쓰면 여섯 좌석부터 상한에 걸려 **같은 크기의 블록이 사람 수에 따라
 * 다르게 찬다** - 재어 보니 2석 판의 블록이 50개, 8석 판의 블록이 23개였다. 블록이 같으면 밀도도 같아야 하고,
 * 그 밀도가 곧 이상한 곳을 숨길 자리의 수다.
 *
 * 36은 27×27 블록 하나가 **자연스럽게 담는 양**이다(상한 없이 구웠을 때 방의 성격에 따라 26~50개, 평균 36).
 * 예산은 구획에 나뉘고 못 쓴 몫은 다른 구획이 가져가므로(`allocateZoneBudgets`·`fillAllZones`)
 * 이 값은 한 구획의 상한이 아니라 **한 사람 몫의 기준선**이다.
 */
export const OBJECTS_PER_ZONE = 36

/**
 * 구획 예산에서 균등분이 차지하는 비율. 나머지는 면적 비례다.
 *
 * 순수 면적 비례면 복도(240셀)가 8개, 거실(936셀)이 29개가 되어 목표 밀도를 양쪽으로 벗어나고,
 * 순수 균등이면 복도와 거실이 같은 개수를 갖는다. 0.6에서 기본 평면도가 **구획당 15~24**로
 * 들어오며, 이 값이 곧 "실제 집은 방 하나에 눈에 보이는 물건이 20~30개"라는 기준선이다.
 */
const EVEN_BUDGET_SHARE = 0.6

/** 플레이어 몸높이(1.75m). 이 구간을 침범하는 오브젝트만 통행을 막는 것으로 본다. */
const PLAYER_HEIGHT_CELLS = 7
/** 넘어갈 수 있는 높이(0.25m). 러그·바닥에 놓인 책은 길을 막지 않는다. */
const STEP_OVER_CELLS = 1
/** 예약 통로의 반폭. 1이면 3칸(0.75m) 폭이라 통행 검사(모든 구획 도달)가 요구하는 최소 폭이다. */
const CORRIDOR_HALF_WIDTH_CELLS = 1

/** `sizeClass`가 없는 카탈로그(외부·테스트 픽스처)를 위한 footprint 폴백 임계값. */
const FLAT_MAX_HEIGHT_CELLS = 1
const FLAT_MIN_FOOTPRINT_AREA = 8
const PROP_MAX_FOOTPRINT_AREA = 4
const PROP_MAX_HEIGHT_CELLS = 4
const ANCHOR_MIN_FOOTPRINT_AREA = 16
const ANCHOR_MIN_HEIGHT_CELLS = 6

/** 소품 하나를 놓기 위해 카탈로그를 다시 뽑아 보는 횟수. 종을 바꾸면 크기가 바뀌어 들어갈 수도 있다. */
const MAX_ENTRY_ATTEMPTS = 3
/** 한 소품이 시도해 볼 관계 후보 수의 상한. 후보 목록은 선호도 순이라 앞쪽만 봐도 충분하다. */
const MAX_CANDIDATE_TRIES = 24
/** 통행 복구 시도 횟수 상한. 구조적으로 통로가 예약돼 있어 보통 0회로 끝난다. */
const MAX_UNBLOCK_PASSES = 4
/** 태그가 이 값을 포함하면 어느 구역에나 어울리는 공용 소품으로 본다. */
const UNIVERSAL_TAG = 'any'

/**
 * 가중치 → 덱에 넣을 장 수. **상한이 3인 것이 이 표의 전부다.**
 * 가중치를 그대로 장 수로 쓰면 10인 소품이 1인 소품의 열 배로 나와, 종을 늘려 얻은 다양성이
 * 그 순간 되감긴다. 흔함은 세 단계로만 표현한다.
 */
const MIN_USABLE_WEIGHT = 1
const DEFAULT_ENTRY_WEIGHT = 5
const WEIGHT_PER_DECK_COPY = 4
const MAX_DECK_COPIES = 3
/** 덱 키의 구분자. 구역 인덱스와 선택자 키를 잇는다. */
const DECK_KEY_SEPARATOR = ':'
/** id 목록이 없는 선택자의 덱 키 접두사. id 기반 키와 절대 겹치지 않게 한다. */
const CLASS_KEY_PREFIX = '#'

/** 생성기 전용 룰 설정. 라운드 설정이 아니라 **verbs 게이트를 통과하기 위한 값**이다. */
const GENERATOR_CONFIG: RoomConfig = {
  allowedVerbs: new Set(['add', 'remove']),
  changeCount: 0,
  placementsPerPlayer: 0,
}

/* --- 내부 타입 -------------------------------------------------------------- */

/** 배치된 오브젝트의 생성기 측 기록. 통행 검사·관계 참조에 쓰는 파생 정보다. */
type PlacedRecord = Readonly<{
  id: InstanceId
  entry: CatalogEntry
  cell: Cell3
  rot: Rot
  /** 회전이 적용된 점유 크기. 관계 해석기가 앵커 크기로 그대로 쓴다. */
  size: Cell3
  zoneIndex: number
  /** 벽에 붙은 배치만 값을 갖는다. `alongWall`·`wallMounted`가 같은 벽을 찾을 때 쓴다. */
  wallSide: WallSide | null
  /** 점유하는 평면 컬럼 목록(중복 제거됨). */
  columns: readonly ColumnKey[]
  /** 플레이어 몸높이 구간을 침범하는가 = 통행을 막는가. */
  blocksWalking: boolean
}>

/** 구역별 등급 풀. 레시피가 지목한 id가 이 카탈로그에 없을 때의 폴백이다. */
type ZonePools = ReadonlyMap<SizeClassName, readonly CatalogEntry[]>

/**
 * 생성 도중의 작업 상태. `occupancy`·`records`는 제자리 변경한다 -
 * 불변 규칙의 대상은 게임 상태(`RoomState`)이고 이 둘은 생성 중에만 존재하는 파생 인덱스다.
 * `state`만은 항상 verbs가 돌려준 새 객체로 교체한다.
 */
type GenScratch = {
  readonly plan: FloorPlan
  readonly floorY: number
  /** 벽걸이 부착 높이(머리 위). 통행을 막지 않는다는 사실이 이 값에서 나온다. */
  readonly mountY: number
  readonly catalog: Catalog
  readonly rng: Rng
  /** 벽이 깔고 앉은 컬럼. 평면도가 "전체 − 방 − 문"으로 유도해 준 값이다. */
  readonly wallColumns: ReadonlySet<ColumnKey>
  /** 문 개구부 컬럼. 걸을 수는 있지만 놓을 수는 없는 유일한 칸이다. */
  readonly doorColumns: ReadonlySet<ColumnKey>
  readonly zoneOfColumn: ReadonlyMap<ColumnKey, number>
  readonly reservedColumns: ReadonlySet<ColumnKey>
  /** 예약 통로를 만들어진 순서대로. 탐색 시작점을 결정론적으로 고르는 데 쓴다. */
  readonly reservedOrder: readonly { x: number; z: number }[]
  readonly occupancy: Map<string, InstanceId>
  readonly records: PlacedRecord[]
  readonly verbCtx: VerbContext
  state: RoomState
  nextInstanceSeq: number
}

/** 구획 하나의 채움 상태. 1차·2차 순회가 같은 상태를 이어서 쓴다. */
type ZoneFill = {
  readonly index: number
  readonly room: Room
  /** 레시피 id → 이 구획에서 몇 번 썼는가. `maxPerZone`이 이 값을 본다. */
  readonly used: Map<string, number>
  placed: number
}

/* --- 순수 헬퍼 -------------------------------------------------------------- */

function rectCenter(rect: CellRect): { x: number; z: number } {
  return { x: rect.x + Math.floor(rect.sizeX / 2), z: rect.z + Math.floor(rect.sizeZ / 2) }
}

/** 사각 영역의 모든 컬럼을 x → z 순으로. 이 순서가 곧 결정론의 일부다. */
function* rectColumns(rect: CellRect): Generator<{ x: number; z: number }> {
  for (let x = rect.x; x < rect.x + rect.sizeX; x += 1) {
    for (let z = rect.z; z < rect.z + rect.sizeZ; z += 1) {
      yield { x, z }
    }
  }
}

/** 상하좌우 4방향 이웃. 대각선은 통행 경로가 아니다(모서리를 뚫고 지나갈 수 없다). */
function orthogonalNeighbors(x: number, z: number): readonly { x: number; z: number }[] {
  return [{ x: x + 1, z }, { x: x - 1, z }, { x, z: z + 1 }, { x, z: z - 1 }]
}

/**
 * 카탈로그 레이어가 코어 규격 위에 얹는 배치 힌트.
 *
 * `catalog/entryTypes.ts`를 import하지 않고 구조적으로 읽는 이유: 생성기는 코어이고
 * 카탈로그는 그 위 레이어다. 무엇보다 이 함수는 **아무 `Catalog`나 받아도 굴러가야** 한다.
 * 그래서 필드가 없으면 던지지 않고 footprint에서 파생한 폴백으로 물러난다.
 */
type PlacementHints = CatalogEntry &
  Readonly<{
    tags?: readonly string[]
    sizeClass?: string
    weight?: number
  }>

/** 카탈로그 항목의 태그. 태그 없는 항목은 **공용**으로 본다. */
function entryTags(entry: CatalogEntry): readonly string[] {
  return (entry as PlacementHints).tags ?? []
}

function entrySizeClass(entry: CatalogEntry): string | undefined {
  const value = (entry as PlacementHints).sizeClass
  return typeof value === 'string' ? value : undefined
}

/**
 * 배치 빈도 가중치. 없거나 규격을 벗어나면 **기본 가중치**로 본다 -
 * 여기서 던지면 카탈로그 한 줄의 오타가 방 생성 전체를 막아 버린다.
 */
function entryWeight(entry: CatalogEntry): number {
  const value = (entry as PlacementHints).weight
  if (typeof value !== 'number' || !Number.isFinite(value) || value < MIN_USABLE_WEIGHT) {
    return DEFAULT_ENTRY_WEIGHT
  }
  return value
}

/** 이 소품이 이 구획 컨셉에 어울리는가. 주방에 침대가 놓이는 것을 막는 유일한 장치다. */
function matchesRoom(entry: CatalogEntry, roomTag: string): boolean {
  const tags = entryTags(entry)
  if (tags.length === 0) return true
  return tags.includes(UNIVERSAL_TAG) || tags.includes(roomTag)
}

function footprintArea(entry: CatalogEntry): number {
  return entry.footprint.x * entry.footprint.z
}

/** 부착면 등급. **선언된 `sizeClass`가 먼저다** - 액자와 협탁은 부피가 비슷하지만 붙는 면이 다르다. */
function classifyEntry(entry: CatalogEntry): SizeClassName {
  switch (entrySizeClass(entry)) {
    case 'anchor':
      return 'anchor'
    case 'floor':
      return 'floor'
    case 'surface':
      return 'surface'
    case 'wall':
      return 'wall'
    case 'ceiling':
      return 'ceiling'
    default:
      return classifyByFootprint(entry)
  }
}

/**
 * `sizeClass`가 없는 카탈로그용 폴백. **순서가 의미를 갖는다.**
 * 소품을 먼저 본다 - 키보드는 납작하지만 러그가 아니라 책상에 얹힐 물건이다.
 */
function classifyByFootprint(entry: CatalogEntry): SizeClassName {
  const height = entry.footprint.y
  const area = footprintArea(entry)

  if (area <= PROP_MAX_FOOTPRINT_AREA && height <= PROP_MAX_HEIGHT_CELLS) return 'surface'
  if (height <= FLAT_MAX_HEIGHT_CELLS && area >= FLAT_MIN_FOOTPRINT_AREA) return 'floor'
  if (area >= ANCHOR_MIN_FOOTPRINT_AREA || height >= ANCHOR_MIN_HEIGHT_CELLS) return 'anchor'
  return 'floor'
}

/**
 * 통행을 막는가. 발목 높이 이하는 넘어가고 몸높이 위는 밑으로 지나간다 -
 * 그 사이를 침범할 때만 막힌 것으로 본다. 러그가 복도를 막지 않게 하는 규칙이 이것이다.
 */
function blocksWalking(cellY: number, sizeY: number, floorY: number): boolean {
  const top = cellY + sizeY
  return top > floorY + STEP_OVER_CELLS && cellY < floorY + PLAYER_HEIGHT_CELLS
}

function uniqueColumns(cells: readonly Cell3[]): readonly ColumnKey[] {
  const seen = new Set<ColumnKey>()
  for (const cell of cells) seen.add(footprintKey(cell.x, cell.z))
  return [...seen]
}

/** 배치 기록을 관계 해석기가 읽는 앵커로. 좌표 규약이 같으므로 필드를 골라 담기만 하면 된다. */
function toAnchor(record: PlacedRecord): GroupAnchor {
  return { cell: record.cell, size: record.size, rot: record.rot, wallSide: record.wallSide }
}

/* --- 준비 단계 -------------------------------------------------------------- */

/** 컬럼 → 구획 인덱스. 구획이 겹치면 먼저 선언된 쪽이 이긴다(평면도 결함의 조용한 폭발 방지). */
function buildZoneIndex(plan: FloorPlan): ReadonlyMap<ColumnKey, number> {
  const index = new Map<ColumnKey, number>()

  plan.rooms.forEach((room, roomIndex) => {
    for (const { x, z } of rectColumns(room.rect)) {
      const key = footprintKey(x, z)
      if (!index.has(key)) index.set(key, roomIndex)
    }
  })

  return index
}

/** 모든 문 개구부가 덮는 컬럼. */
function collectDoorColumns(plan: FloorPlan): ReadonlySet<ColumnKey> {
  const doors = new Set<ColumnKey>()
  for (const doorway of plan.doorways) {
    for (const cell of doorwayCells(doorway)) doors.add(footprintKey(cell.x, cell.z))
  }
  return doors
}

type ColumnMap = Map<ColumnKey, { x: number; z: number }>

function reserveWidened(target: ColumnMap, x: number, z: number): void {
  for (let dx = -CORRIDOR_HALF_WIDTH_CELLS; dx <= CORRIDOR_HALF_WIDTH_CELLS; dx += 1) {
    for (let dz = -CORRIDOR_HALF_WIDTH_CELLS; dz <= CORRIDOR_HALF_WIDTH_CELLS; dz += 1) {
      target.set(footprintKey(x + dx, z + dz), { x: x + dx, z: z + dz })
    }
  }
}

/** 두 점을 잇는 L자 경로를 예약한다. 구역 영역이 사각형이므로 경로는 그 안에 머문다. */
function reserveLPath(
  target: ColumnMap,
  from: { x: number; z: number },
  to: { x: number; z: number },
): void {
  reserveWidened(target, from.x, from.z)

  let x = from.x
  while (x !== to.x) {
    x += x < to.x ? 1 : -1
    reserveWidened(target, x, from.z)
  }
  let z = from.z
  while (z !== to.z) {
    z += z < to.z ? 1 : -1
    reserveWidened(target, to.x, z)
  }
}

/**
 * 통행 예약 컬럼. **배치 전에** 확정되므로 연결성이 구조적으로 보장된다.
 * 문 컬럼 전부 + **문 하나당 인접 구역 중심까지 L자 통로 하나**를 예약한다.
 *
 * 통로를 문 칸마다 긋지 않는 이유가 이 함수의 핵심이다. 문 폭이 6칸이면 통로도 여섯 줄이 되고,
 * 폭 3칸짜리 줄 여섯 개가 서로 벌어져 **복도 구획 전체가 예약되는** 일이 실제로 벌어졌다
 * (그 복도에는 가구가 한 점도 들어가지 못했다). 개구부 자체가 이미 3칸 폭으로 예약돼 있어
 * 서로 이어져 있으므로, 대표 칸 하나에서 중심까지만 이으면 연결성은 그대로 보장된다.
 */
function buildReservedColumns(
  plan: FloorPlan,
  zoneOfColumn: ReadonlyMap<ColumnKey, number>,
): readonly { x: number; z: number }[] {
  const reserved = new Map<ColumnKey, { x: number; z: number }>()

  for (const doorway of plan.doorways) {
    const cells = doorwayCells(doorway)
    for (const door of cells) reserveWidened(reserved, door.x, door.z)

    const representative = cells[Math.floor(cells.length / 2)]
    if (representative === undefined) continue

    const linked = new Set<number>()
    for (const neighbor of orthogonalNeighbors(representative.x, representative.z)) {
      const zoneIndex = zoneOfColumn.get(footprintKey(neighbor.x, neighbor.z))
      if (zoneIndex === undefined || linked.has(zoneIndex)) continue
      const room = plan.rooms[zoneIndex]
      if (room === undefined) continue
      linked.add(zoneIndex)
      reserveLPath(reserved, neighbor, rectCenter(room.rect))
    }
  }

  return [...reserved.values()]
}

/**
 * 구역별 등급 풀. 카탈로그를 id로 정렬해 순회 순서에 결과가 의존하지 않게 한다 -
 * 같은 카탈로그를 다른 순서로 만들어도 같은 시드면 같은 방이어야 한다.
 */
function buildZonePools(plan: FloorPlan, catalog: Catalog): readonly ZonePools[] {
  const entries = [...catalog.values()].sort((a, b) => compareCodePoint(a.id, b.id))

  return plan.rooms.map((room) => {
    const pools = new Map<SizeClassName, CatalogEntry[]>()
    for (const entry of entries) {
      if (!matchesRoom(entry, room.tag)) continue
      const key = classifyEntry(entry)
      const pool = pools.get(key) ?? []
      pool.push(entry)
      pools.set(key, pool)
    }
    return pools
  })
}

/* --- 소품 덱 ---------------------------------------------------------------- */

/** 선택자 하나에서 한 장씩 빼 주는 배분기. `null`이면 그 구역에 후보가 없다는 뜻이다. */
type EntryDealer = (zoneIndex: number, select: EntrySelector) => CatalogEntry | null

/** 가중치를 장 수로. 1~4 → 1장 / 5~8 → 2장 / 9~10 → 3장. */
function deckCopies(weight: number): number {
  return Math.min(MAX_DECK_COPIES, Math.max(1, Math.ceil(weight / WEIGHT_PER_DECK_COPY)))
}

/** 흔한 소품이 더 자주 나오도록 장 수를 늘린 덱. 순서는 아직 원본 순서다(섞는 것은 호출부). */
function buildWeightedDeck(pool: readonly CatalogEntry[]): CatalogEntry[] {
  const deck: CatalogEntry[] = []
  for (const entry of pool) {
    for (let copy = 0; copy < deckCopies(entryWeight(entry)); copy += 1) deck.push(entry)
  }
  return deck
}

/**
 * 선택자의 덱 키. **id 목록이 같으면 같은 덱을 쓴다** - 레시피가 달라도 "식탁 위 소품"은
 * 한 구획 안에서 같은 반복 억제를 받아야 한다.
 */
function selectorKey(select: EntrySelector): string {
  const ids = select.ids ?? []
  if (ids.length > 0) return ids.join(',')
  return `${CLASS_KEY_PREFIX}${select.sizeClass ?? ''}`
}

/**
 * 선택자가 이 구획에서 실제로 쓸 수 있는 후보들.
 * **id가 먼저**이고, 카탈로그에 없거나 태그가 안 맞아 하나도 남지 않으면 등급 풀로 물러난다.
 */
function resolveSelectorPool(
  catalog: Catalog,
  pools: ZonePools | undefined,
  roomTag: string,
  select: EntrySelector,
): readonly CatalogEntry[] {
  const byId: CatalogEntry[] = []
  for (const id of select.ids ?? []) {
    const entry = catalog.get(id)
    if (entry !== undefined && matchesRoom(entry, roomTag)) byId.push(entry)
  }
  if (byId.length > 0) return byId
  if (select.sizeClass === undefined) return []
  return pools?.get(select.sizeClass) ?? []
}

/**
 * 구역·선택자별 소품 **덱**을 만든다. 매번 `rng.pick`으로 뽑지 않는 이유가 이 함수의 전부다.
 *
 * 균등 추출은 풀이 클 때만 균등하다. 풀이 작은 구획에서는 같은 소품이 계속 나오는데,
 * 실측에서 실제로 그랬다 - 욕실 소품 풀이 1종이던 시점에 **욕실 71개 중 62개가 휴지통**이었다.
 * 그러면 술래는 형태가 아니라 **개수 세기**로 풀게 되고 동사별 난이도 차이가 무너진다.
 *
 * 그래서 뽑는 대신 **섞은 덱에서 한 장씩 뺀다.** 덱이 비면 다시 섞는다.
 */
function createEntryDealer(
  gen: GenScratch,
  pools: readonly ZonePools[],
  tags: readonly string[],
): EntryDealer {
  const decks = new Map<string, CatalogEntry[]>()

  return (zoneIndex, select) => {
    const key = `${String(zoneIndex)}${DECK_KEY_SEPARATOR}${selectorKey(select)}`
    let deck = decks.get(key)

    if (deck === undefined || deck.length === 0) {
      const pool = resolveSelectorPool(
        gen.catalog,
        pools[zoneIndex],
        tags[zoneIndex] ?? '',
        select,
      )
      if (pool.length === 0) return null
      deck = gen.rng.shuffle(buildWeightedDeck(pool))
      decks.set(key, deck)
    }

    return deck.pop() ?? null
  }
}

/* --- 배치 ------------------------------------------------------------------- */

function nextInstanceId(gen: GenScratch): InstanceId {
  const seq = gen.nextInstanceSeq
  gen.nextInstanceSeq += 1
  return `${INSTANCE_ID_PREFIX}${String(seq).padStart(INSTANCE_ID_DIGITS, '0')}`
}

/**
 * 한 번의 배치 시도. 성공하면 기록, 실패하면 `null`.
 * 게임 규칙 검증(경계·셀 점유)은 전부 `applyAdd` → `canPlace`가 한다. 여기서 더 보는 것은
 * 생성기만의 정책 셋뿐이다: 벽·문 컬럼 금지 / 구역 이탈 금지(걸치면 태그 규칙이 무의미해진다) /
 * 예약 통로에 통행 방해물 금지.
 */
function tryPlace(
  gen: GenScratch,
  entry: CatalogEntry,
  candidate: Candidate,
  zoneIndex: number,
): PlacedRecord | null {
  const { cell, rot } = candidate
  const cells = occupiedCells(cell, entry, rot)
  const columns = uniqueColumns(cells)
  const size = rotatedFootprint(entry.footprint, rot)
  const isBlocking = blocksWalking(cell.y, size.y, gen.floorY)

  for (const column of columns) {
    if (gen.wallColumns.has(column)) return null
    if (gen.doorColumns.has(column)) return null
    if (gen.zoneOfColumn.get(column) !== zoneIndex) return null
    if (isBlocking && gen.reservedColumns.has(column)) return null
  }

  const object: ObjectInstance = {
    id: nextInstanceId(gen),
    catalogId: entry.id,
    cell,
    rot,
    colorId: gen.rng.int(0, PALETTE_SIZE - 1),
    placedBy: SYSTEM_PLACER,
  }

  const result = applyAdd(gen.state, object, gen.verbCtx)
  if (!result.ok) return null

  gen.state = result.state
  for (const occupied of cells) gen.occupancy.set(cellKey(occupied), object.id)

  const record: PlacedRecord = {
    id: object.id,
    entry,
    cell,
    rot,
    size,
    zoneIndex,
    wallSide: candidate.wallSide,
    columns,
    blocksWalking: isBlocking,
  }
  gen.records.push(record)
  return record
}

/** 배치 취소. verbs를 경유하고 파생 인덱스도 함께 되돌린다. */
function removePlaced(gen: GenScratch, record: PlacedRecord): void {
  const result = applyRemove(gen.state, record.id, gen.verbCtx)
  if (!result.ok) {
    throw new Error(`베이스 룸 생성: 배치 취소 실패 (${record.id}, 사유 ${result.reason})`)
  }

  gen.state = result.state
  for (const cell of occupiedCells(record.cell, record.entry, record.rot)) {
    gen.occupancy.delete(cellKey(cell))
  }

  const index = gen.records.indexOf(record)
  if (index >= 0) gen.records.splice(index, 1)
}

/* --- 그룹 배치 -------------------------------------------------------------- */

/** 그룹 배치 중의 역할 → 기록. 관계가 좌표가 아니라 이 표를 통해 서로를 참조한다. */
type RoleTable = Map<SlotRole, PlacedRecord[]>

/**
 * 관계가 가리키는 앵커를 찾는다. 같은 역할이 여러 번 놓였으면 **가장 최근 것**이다.
 *
 * 최근 것을 쓰는 규칙이 조리대·욕실 설비의 "줄" 배치를 만든다: `alongWall('counter-run')`처럼
 * 슬롯이 자기 자신을 참조하면 두 번째 가구부터 직전 가구 옆에 붙고, 첫 번째는 참조할 것이 없으므로
 * 그룹 앵커 옆에 붙는다. 규칙 하나로 체인이 생긴다.
 *
 * **그룹 앵커 폴백은 자기 참조에만 준다.** 남의 역할을 가리켰는데 그것이 안 놓였다면
 * 그 슬롯은 통째로 건너뛴다 - 폴백을 열어 두면 "TV장이 못 들어갔으니 TV를 소파 위에" 같은
 * 배치가 나오고, 그건 이번 개편이 없애려는 중구난방 그 자체다.
 */
function findAnchor(
  roles: RoleTable,
  role: SlotRole,
  ownRole: SlotRole,
  groupAnchor: PlacedRecord | undefined,
): GroupAnchor | null {
  const placements = roles.get(role) ?? []
  const latest = placements[placements.length - 1]
  const source = latest ?? (role === ownRole ? groupAnchor : undefined)
  return source === undefined ? null : toAnchor(source)
}

/** 슬롯 하나에서 소품 한 개를 놓는다. 종을 바꿔 가며 몇 번 시도하고, 안 되면 `null`. */
function placeSlotItem(
  gen: GenScratch,
  zone: ZoneFill,
  groupSlot: GroupSlot,
  roles: RoleTable,
  groupAnchor: PlacedRecord | undefined,
  deal: EntryDealer,
): PlacedRecord | null {
  const anchorRole = relationAnchorRole(groupSlot.relation)
  const anchor =
    anchorRole === null ? null : findAnchor(roles, anchorRole, groupSlot.role, groupAnchor)
  if (anchorRole !== null && anchor === null) return null

  for (let attempt = 0; attempt < MAX_ENTRY_ATTEMPTS; attempt += 1) {
    const entry = deal(zone.index, groupSlot.select)
    if (entry === null) return null

    const candidates = resolveRelation(groupSlot.relation, {
      zone: zone.room.rect,
      footprint: entry.footprint,
      floorY: gen.floorY,
      mountY: gen.mountY,
      ceilingY: gen.plan.bounds.sizeY,
      anchor,
      rng: gen.rng,
    })

    for (const candidate of candidates.slice(0, MAX_CANDIDATE_TRIES)) {
      const record = tryPlace(gen, entry, candidate, zone.index)
      if (record !== null) return record
    }
  }

  return null
}

/** 슬롯 i 이후에 **반드시** 더 놓아야 하는 개수. 앞 슬롯이 예산을 다 먹지 않게 막는다. */
function suffixMinimums(recipe: GroupRecipe): readonly number[] {
  const suffix: number[] = new Array<number>(recipe.slots.length).fill(0)
  for (let index = recipe.slots.length - 2; index >= 0; index -= 1) {
    suffix[index] = (suffix[index + 1] ?? 0) + (recipe.slots[index + 1]?.min ?? 0)
  }
  return suffix
}

/**
 * 그룹 하나를 통째로 놓는다. 성공하면 놓은 개수, 실패하면 0(놓았던 것은 전부 되돌린다).
 *
 * **부분 배치를 남기지 않는 것이 이 함수의 계약이다.** 개수를 채우려고 그룹을 반토막 내면
 * "침대는 있는데 협탁만 공중에 뜬" 배치가 남고, 그게 바로 이번 개편이 없애려는 중구난방이다.
 */
function placeGroup(
  gen: GenScratch,
  zone: ZoneFill,
  recipe: GroupRecipe,
  deal: EntryDealer,
  allowance: number,
): number {
  const placed: PlacedRecord[] = []
  const roles: RoleTable = new Map()
  const suffix = suffixMinimums(recipe)

  const rollback = (): number => {
    for (const record of [...placed].reverse()) removePlaced(gen, record)
    return 0
  }

  for (let index = 0; index < recipe.slots.length; index += 1) {
    const groupSlot = recipe.slots[index]
    if (groupSlot === undefined) continue

    const limit = Math.min(groupSlot.max, allowance - placed.length - (suffix[index] ?? 0))
    let count = 0

    while (count < limit) {
      const record = placeSlotItem(gen, zone, groupSlot, roles, placed[0], deal)
      if (record === null) break

      placed.push(record)
      const bucket = roles.get(groupSlot.role) ?? []
      bucket.push(record)
      roles.set(groupSlot.role, bucket)
      count += 1
    }

    if (count < groupSlot.min) return rollback()
  }

  return placed.length
}

/**
 * 한 구획을 `capacity`개까지 채운다. 추가로 놓은 개수를 돌려준다.
 *
 * 우선순위 낮은 단계부터, 그 안에서는 **라운드 로빈**으로 한 바퀴씩 돌린다.
 * 가중 추첨을 쓰지 않는 이유: 추첨은 같은 그룹을 연달아 뽑아 "소파 세 세트짜리 거실"을 만들고,
 * 그러면 구획마다 성격이 다르다는 성질이 사라진다. 실패한 레시피는 그 구획에서 접는다 -
 * 앵커가 안 들어갔다는 것은 자리가 없다는 뜻이라 다시 시도해도 같은 결과다.
 */
function fillZone(
  gen: GenScratch,
  zone: ZoneFill,
  capacity: number,
  deal: EntryDealer,
  recipes: RecipeBook,
): number {
  const book = recipes.get(zone.room.tag) ?? []
  if (book.length === 0) return 0

  const tiers = [...new Set(book.map((recipe) => recipe.priority))].sort((a, b) => a - b)
  let added = 0

  for (const tier of tiers) {
    const tierRecipes = gen.rng.shuffle(book.filter((recipe) => recipe.priority === tier))
    let progressed = true

    while (progressed && zone.placed < capacity) {
      progressed = false

      for (const recipe of tierRecipes) {
        const allowance = capacity - zone.placed
        if (allowance <= 0) break
        if ((zone.used.get(recipe.id) ?? 0) >= recipe.maxPerZone) continue
        if (recipeMinSize(recipe) > allowance) continue

        const count = placeGroup(gen, zone, recipe, deal, allowance)
        if (count === 0) {
          zone.used.set(recipe.id, recipe.maxPerZone)
          continue
        }

        zone.used.set(recipe.id, (zone.used.get(recipe.id) ?? 0) + 1)
        zone.placed += count
        added += count
        progressed = true
      }
    }
  }

  return added
}

/**
 * 목표 개수를 구획에 나눈다. 면적 비례와 균등분을 절반씩 섞고, 최대잔여법으로 정수화해
 * **합이 정확히 목표와 같게** 만든다(반올림만 하면 구획 수만큼 어긋난다).
 */
function allocateZoneBudgets(plan: FloorPlan, target: number): readonly number[] {
  const areas = plan.rooms.map((room) => room.rect.sizeX * room.rect.sizeZ)
  const totalArea = areas.reduce((sum, area) => sum + area, 0)
  const count = plan.rooms.length

  const raw = areas.map((area) => {
    const areaShare = totalArea > 0 ? area / totalArea : 1 / count
    return target * (EVEN_BUDGET_SHARE / count + (1 - EVEN_BUDGET_SHARE) * areaShare)
  })

  const budgets = raw.map((value) => Math.floor(value))
  let rest = target - budgets.reduce((sum, value) => sum + value, 0)

  const byFraction = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)

  for (const { index } of byFraction) {
    if (rest <= 0) break
    budgets[index] = (budgets[index] ?? 0) + 1
    rest -= 1
  }

  return budgets
}

/**
 * 모든 구획을 채운다. 1차는 배분된 예산대로, 2차는 **남은 몫**을 다시 나눈다 -
 * 자리가 없어 예산을 못 쓴 구획이 있으면 그만큼 다른 구획이 가져가야 목표에 가까워진다.
 */
function fillAllZones(
  gen: GenScratch,
  zones: readonly ZoneFill[],
  target: number,
  deal: EntryDealer,
  recipes: RecipeBook,
): void {
  const budgets = allocateZoneBudgets(gen.plan, target)
  let total = 0

  zones.forEach((zone, index) => {
    const capacity = Math.min(budgets[index] ?? 0, target - total)
    total += fillZone(gen, zone, capacity, deal, recipes)
  })

  let leftover = target - total
  if (leftover <= 0 || zones.length === 0) return

  const share = Math.ceil(leftover / zones.length)
  for (const zone of zones) {
    if (leftover <= 0) break
    const capacity = zone.placed + Math.min(share, leftover)
    leftover -= fillZone(gen, zone, capacity, deal, recipes)
  }
}

/* --- 통행 검증 (안전망) ------------------------------------------------------ */

/** 플레이어가 서 있을 수 있는 후보 컬럼 = 구역 영역 ∪ 문 컬럼 − 벽 컬럼. */
function collectWalkableColumns(gen: GenScratch): ReadonlySet<ColumnKey> {
  const walkable = new Set<ColumnKey>()
  for (const key of [...gen.zoneOfColumn.keys(), ...gen.doorColumns]) {
    if (!gen.wallColumns.has(key)) walkable.add(key)
  }
  return walkable
}

/** 통행 방해물이 점유한 컬럼. */
function collectBlockedColumns(records: readonly PlacedRecord[]): ReadonlySet<ColumnKey> {
  const blocked = new Set<ColumnKey>()
  for (const record of records) {
    if (record.blocksWalking) for (const column of record.columns) blocked.add(column)
  }
  return blocked
}

/** 4방향 너비 우선 탐색. 시작 컬럼에서 실제로 걸어갈 수 있는 컬럼 집합을 돌려준다. */
function floodFill(
  walkable: ReadonlySet<ColumnKey>,
  blocked: ReadonlySet<ColumnKey>,
  start: { x: number; z: number },
): ReadonlySet<ColumnKey> {
  const queue: { x: number; z: number }[] = [start]
  const reached = new Set<ColumnKey>([footprintKey(start.x, start.z)])

  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head]
    if (current === undefined) continue

    for (const neighbor of orthogonalNeighbors(current.x, current.z)) {
      const key = footprintKey(neighbor.x, neighbor.z)
      if (reached.has(key) || !walkable.has(key) || blocked.has(key)) continue
      reached.add(key)
      queue.push(neighbor)
    }
  }

  return reached
}

/**
 * 탐색 시작점. **예약 통로에서 출발한다** - 통로는 서로 이어져 있고 방해물이 놓이지 않으므로
 * 반드시 본 통행망 위의 점이다. 구역 모서리에서 출발하면 소파 뒤 몇 칸짜리 포켓에 갇혀
 * "방 전체가 도달 불가"로 오판하고, 그 오판이 안전망을 깨워 가구를 무더기로 지운다.
 */
function findStartColumn(
  gen: GenScratch,
  walkable: ReadonlySet<ColumnKey>,
  blocked: ReadonlySet<ColumnKey>,
): { x: number; z: number } | null {
  for (const { x, z } of gen.reservedOrder) {
    const key = footprintKey(x, z)
    if (walkable.has(key) && !blocked.has(key)) return { x, z }
  }

  for (const room of gen.plan.rooms) {
    for (const { x, z } of rectColumns(room.rect)) {
      const key = footprintKey(x, z)
      if (walkable.has(key) && !blocked.has(key)) return { x, z }
    }
  }
  return null
}

/** 한 칸도 도달하지 못한 구역의 인덱스 목록. */
function findUnreachableZones(gen: GenScratch, reached: ReadonlySet<ColumnKey>): readonly number[] {
  const isReached = (room: Room): boolean => {
    for (const { x, z } of rectColumns(room.rect)) {
      if (reached.has(footprintKey(x, z))) return true
    }
    return false
  }

  return gen.plan.rooms.flatMap((room, zoneIndex) => (isReached(room) ? [] : [zoneIndex]))
}

/**
 * 모든 구역이 도달 가능한지 확인하고, 막혔으면 그 구역의 통행 방해 오브젝트를 제거한다.
 * 예약 통로 덕분에 보통은 첫 검사에서 통과하고 아무것도 제거하지 않는다. 그래도 두는 이유는
 * 평면도 결함(문 없는 구역 등)이 들어왔을 때 조용히 진행 불가능한 방을 내보내지 않기 위해서다.
 */
function ensureAllZonesReachable(gen: GenScratch): void {
  for (let pass = 0; pass < MAX_UNBLOCK_PASSES; pass += 1) {
    const walkable = collectWalkableColumns(gen)
    const blocked = collectBlockedColumns(gen.records)
    const start = findStartColumn(gen, walkable, blocked)
    if (start === null) return

    const reached = floodFill(walkable, blocked, start)
    const unreachable = findUnreachableZones(gen, reached)
    if (unreachable.length === 0) return

    const targets = new Set(unreachable)
    const victims = gen.records.filter(
      (record) => record.blocksWalking && targets.has(record.zoneIndex),
    )
    // 제거할 것이 없는데 여전히 도달 불가라면 배치가 원인이 아니다(문 없는 구역 등).
    if (victims.length === 0) return

    for (const victim of [...victims].reverse()) {
      removePlaced(gen, victim)
    }
  }
}

/* --- 진입점 ----------------------------------------------------------------- */

function assertValidTarget(target: number): void {
  if (!Number.isInteger(target) || target < 0) {
    throw new RangeError(`generateBaseRoom: targetObjectCount는 0 이상의 정수여야 한다: ${String(target)}`)
  }
}

/**
 * 시드로부터 실내를 생성한다. **같은 시드 + 같은 평면도 + 같은 카탈로그 → 같은 방.**
 *
 * `targetObjectCount`는 상한이다 - 그룹이 들어갈 자리가 없으면 이보다 적게 나온다.
 * 억지로 채우지 않는 것이 의도다(파일 상단 "부분 배치를 남기지 않는다").
 */
export function generateBaseRoom(
  plan: FloorPlan,
  seed: number | string,
  catalog: Catalog,
  options: GenerateOptions = {},
): RoomState {
  if (plan.rooms.length === 0) {
    throw new RangeError('generateBaseRoom: 구획이 하나도 없는 평면도로는 방을 만들 수 없다')
  }

  const target = options.targetObjectCount ?? DEFAULT_TARGET_OBJECT_COUNT
  assertValidTarget(target)

  const zoneOfColumn = buildZoneIndex(plan)
  const occupancy = new Map<string, InstanceId>()
  const rng = createRng(seed)
  const reserved = buildReservedColumns(plan, zoneOfColumn)
  const floorY = options.floorY ?? DEFAULT_FLOOR_Y

  const gen: GenScratch = {
    plan,
    floorY,
    mountY: floorY + PLAYER_HEIGHT_CELLS,
    catalog,
    rng,
    wallColumns: wallFootprintCells(plan),
    doorColumns: collectDoorColumns(plan),
    zoneOfColumn,
    reservedColumns: new Set(reserved.map(({ x, z }) => footprintKey(x, z))),
    reservedOrder: reserved,
    occupancy,
    records: [],
    verbCtx: {
      catalog,
      config: GENERATOR_CONFIG,
      bounds: plan.bounds,
      // 같은 Map 인스턴스를 넘긴다. 배치마다 갱신되므로 verbs의 점유 검사가 항상 최신을 본다.
      occupancy,
      // 시스템 배치는 꾸미기 페이즈의 인당 제한 대상이 아니다.
      shouldEnforcePlacementLimit: false,
    },
    state: createRoom(plan.id),
    nextInstanceSeq: 0,
  }

  const pools = buildZonePools(plan, catalog)
  // 배분기는 **구획 전체가 공유한다.** 덱은 여러 그룹에 걸쳐 종별 개수를 묶는 장치인데
  // 그룹마다 새로 섞으면 그 기억이 매번 지워져 반복 억제가 그룹 안에서만 작동한다.
  const deal = createEntryDealer(gen, pools, plan.rooms.map((room) => room.tag))
  const zones: readonly ZoneFill[] = plan.rooms.map((room, index) => ({
    index,
    room,
    used: new Map<string, number>(),
    placed: 0,
  }))

  fillAllZones(gen, zones, target, deal, options.recipes ?? HOME_RECIPES)
  ensureAllZonesReachable(gen)

  return gen.state
}
